import { ProductStatus } from '@today/types'
import { apiClient } from '@/services/api.client'

/**
 * Offer Wizard Step 2's consumer-owned view of the product catalogue (TO-123
 * REQ-001 / REQ-010). Step 2 depends on this port only — never on the product
 * admin pages, hooks or services — so the catalogue source can change (e.g. the
 * personalization ranker, TO-157) without touching the step.
 */
export interface CatalogueProduct {
	productId: string
	code: string
	name: string
	categoryId: string | null
}

export interface CatalogueCategory {
	categoryId: string
	name: string
}

export interface ProductPort {
	/** Every ACTIVE product of the caller's tenant (all pages). */
	listActive(): Promise<CatalogueProduct[]>
	/** Every product category of the caller's tenant (all pages). */
	listCategories(): Promise<CatalogueCategory[]>
}

interface Page<T> {
	data: T[]
	totalPages: number
}

const PAGE_LIMIT = 100

/** Reads every page of a product-service list; an empty list is `200 {data:[],total:0}` (TO-632, AD-21.7). */
async function readAll<T>(url: string, filters: Record<string, string>): Promise<T[]> {
	const items: T[] = []
	for (let page = 1; ; page++) {
		const response = await apiClient.get<Page<T> | ''>(url, {
			params: { ...filters, limit: String(PAGE_LIMIT), page: String(page) },
		})
		if (!response.data) return items
		items.push(...response.data.data)
		if (page >= response.data.totalPages) return items
	}
}

/** The HTTP adapter: `GET /products?filters[status]=ACTIVE` and `GET /products/categories`. */
export const httpProductPort: ProductPort = {
	async listActive() {
		const rows = await readAll<CatalogueProduct>('/products', { 'filters[status]': ProductStatus.ACTIVE })
		return rows.map(({ productId, code, name, categoryId }) => ({ productId, code, name, categoryId }))
	},
	async listCategories() {
		const rows = await readAll<CatalogueCategory>('/products/categories', {})
		return rows.map(({ categoryId, name }) => ({ categoryId, name }))
	},
}
